import { Button } from "@/components/core/buttons/button";
import { Form, FormDescription, FormField, FormGroup, FormItem, FormLabel, FormMessage, FormPrepend } from "@/components/core/forms/form.tsx";
import { Input } from "@/components/core/forms/input";
import Select from "@/components/core/forms/select";
import { useModalControl } from "@/components/core/modals/use-modal-controller";
import type { CounselFormSchema } from "@/features/case-evaluation/types/counsel";
import { type DefendantFormSchema, defendantFormSchema } from "@/features/case-evaluation/types/defendant";
import { useAddDetailsDefendantMutation } from "@/features/case-record/api/queries/case-records-query";
import { countriesQueryOptions } from "@/features/case-record/api/queries/country-query";
import cityService from "@/features/dashboard/api/services/city-service";
import provinceService from "@/features/dashboard/api/services/province-service";
import regionService from "@/features/dashboard/api/services/region-service";
import userService from "@/features/dashboard/api/services/user-service";
import { queryClient } from "@/query-client";
import { zodResolver } from "@hookform/resolvers/zod";
import { IconAlertCircle } from "@tabler/icons-react";
import { useSuspenseQuery } from "@tanstack/react-query";
import clsx from "clsx";
import { debounce } from "lodash";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { NumericFormat } from "react-number-format";
import Alert from "../core/alert";
import Combobox from "../core/forms/combobox";

export default function CaseDefendantModal({
	data,
	emails,
	isEdit,
	defendantId,
	caseData,
}: {
	data?: DefendantFormSchema;
	isEdit?: boolean;
	emails?: (string | undefined)[];
	defendantId?: string;
	onSubmit: (data: DefendantFormSchema) => void;
}) {
	const { closeModal } = useModalControl();
	const form = useForm<DefendantFormSchema>({
		resolver: zodResolver(defendantFormSchema),
		defaultValues: {
			defendant_id: defendantId,
			first_name: data?.first_name || "",
			last_name: data?.last_name || "",
			middle_name: data?.middle_name || "",
			email: data?.email || "",
			mobile_number: data?.mobile_number || "",
			classification: data?.classification || "Individual",
			corporation: data?.corporation || "",
			barangay: data?.barangay || "",
			address_1: data?.address_1 || "",
			address_2: data?.address_2 || "",
			zipcode: data?.zipcode || "",
			region_code: data?.region_code || "",
			region: data?.region || {},
			province_code: data?.province_code || "",
			province: data?.province || {},
			city_code: data?.city_code || "",
			city: data?.city || {},
			suffix: data?.suffix || "",
			country: data?.country || "",
			address_type: "domestic",
		},
	});

	const [debouncedEmail, setDebouncedEmail] = useState("");
	const [emailExists, setEmailExists] = useState(false);
	const email = form.watch("email");
	const addressType = form.watch("address_type");

	const countriesQuery = useSuspenseQuery(countriesQueryOptions());

	const countries = countriesQuery.data;

	const updateDebouncedEmail = useCallback(
		debounce(
			(newEmail) => {
				setDebouncedEmail(newEmail);
			},
			500,
			{ trailing: true },
		),
		[],
	);

	useEffect(() => {
		updateDebouncedEmail(email);
	}, [email, updateDebouncedEmail]);

	useEffect(() => {
		if (debouncedEmail) {
			userService
				.validateEmail(debouncedEmail)
				.then((response) => {
					if (response) {
						const user = response;
						setEmailExists(true);

						for (const key in user) {
							const value = user[key as keyof typeof user];
							form.setValue(key as keyof DefendantFormSchema, (value as string) ?? "");
						}
					} else {
						setEmailExists(false);
					}
				})
				.catch((_) => {
					setEmailExists(false);
				});
		}
	}, [debouncedEmail, form]);

	const regionCode = form.watch("region_code");
	const provinceCode = form.watch("province_code");
	const classification = form.watch("classification");

	const sameEmail = emails?.includes(form.watch("email") || "");

	const addDetailsDefendantMutation = useAddDetailsDefendantMutation();
	// const onSubmit = async (data: CounselFormSchema) => {
	// 	await addDetailsDefendantMutation.mutateAsync({
	// 		data,
	// 	queryClient.invalidateQueries({ queryKey: ["case_records"] });
	// 	closeModal();
	// };
	const onSubmit = async (data: CounselFormSchema) => {
		await addDetailsDefendantMutation.mutateAsync({
			data,
			caseData,
		});
		queryClient.invalidateQueries({ queryKey: ["case_records"] });
		closeModal();
	};

	return (
		<Form {...form}>
			<form className="w-full" onSubmit={form.handleSubmit(onSubmit)}>
				<div className="flex flex-col w-full gap-4">
					<div className="flex flex-col w-full rounded-md border border-border/50">
						<div className="flex flex-col items-center justify-between w-full py-3 px-4">
							{!isEdit && sameEmail && undef && (
								<div className="col-span-12 w-full py-1">
									<Alert type="error" allowClose={false} title="Email Address Already Added as Defendant" icon={<IconAlertCircle size={20} stroke={2} />}>
										This email is already added as defendant.
									</Alert>
								</div>
							)}
							{!sameEmail && emailExists && undef && (
								<div className="col-span-12 w-full py-1">
									<Alert type="info" allowClose={false} title="Email Address Already Exists" icon={<IconAlertCircle size={20} stroke={2} />}>
										The system will use the personal information associated with the email address you entered to auto-fill the form fields. If you do not wish
										to use this information, please enter a different email address.
									</Alert>
								</div>
							)}

							<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3">
								<div className="col-span-12 flex">
									<FormField
										control={form.control}
										name="classification"
										render={({ field }) => (
											<FormItem>
												<div className="flex flex-col w-full">
													<FormLabel required>Type</FormLabel>
													<FormDescription>Select type of defendant</FormDescription>
												</div>
												<FormGroup>
													<Select
														onValueChange={field.onChange}
														defaultValue={field.value}
														options={[
															{
																value: "Juridical",
																label: "Juridical",
															},
															{
																value: "Individual",
																label: "Individual",
															},
															{
																value: "Government Agency",
																label: "Government Agency",
															},
														]}
													/>
												</FormGroup>
												<FormMessage />
											</FormItem>
										)}
									/>
								</div>
								{classification && classification === "Individual" && (
									<>
										<div className="col-span-4 flex">
											<FormField
												control={form.control}
												name="first_name"
												render={({ field }) => (
													<FormItem>
														<FormLabel required>First Name</FormLabel>
														<FormGroup iconPosition="left">
															<Input autoComplete="off" {...field} onChange={(e) => field.onChange(e.target.value.toUpperCase())} />
														</FormGroup>
														<FormMessage />
													</FormItem>
												)}
											/>
										</div>

										<div className="col-span-4 flex">
											<FormField
												control={form.control}
												name="middle_name"
												render={({ field }) => (
													<FormItem>
														<FormLabel>Middle Name</FormLabel>
														<FormGroup iconPosition="left">
															<Input autoComplete="off" {...field} onChange={(e) => field.onChange(e.target.value.toUpperCase())} />
														</FormGroup>
														<FormMessage />
													</FormItem>
												)}
											/>
										</div>
										<div className="col-span-3 flex">
											<FormField
												control={form.control}
												name="last_name"
												render={({ field }) => (
													<FormItem>
														<FormLabel required>Last Name</FormLabel>
														<FormGroup iconPosition="left">
															<Input autoComplete="off" {...field} onChange={(e) => field.onChange(e.target.value.toUpperCase())} />
														</FormGroup>
														<FormMessage />
													</FormItem>
												)}
											/>
										</div>
										<div className="col-span-1 flex">
											<FormField
												control={form.control}
												name="suffix"
												render={({ field }) => (
													<FormItem>
														<FormLabel>Suffix</FormLabel>
														<FormGroup iconPosition="left">
															<Input autoComplete="off" {...field} onChange={(e) => field.onChange(e.target.value.toUpperCase())} />
														</FormGroup>
														<FormMessage />
													</FormItem>
												)}
											/>
										</div>
									</>
								)}
								{classification && classification === "Juridical" && (
									<div className="col-span-12 flex">
										<FormField
											control={form.control}
											name="corporation"
											render={({ field }) => (
												<FormItem>
													<div className="flex flex-col w-full">
														<FormLabel required>Name of Juridical Entity</FormLabel>
														<FormDescription />
													</div>
													<FormGroup iconPosition="left">
														<Input autoComplete="off" {...field} onChange={(e) => field.onChange(e.target.value.toUpperCase())} />
													</FormGroup>
													<FormMessage />
												</FormItem>
											)}
										/>
									</div>
								)}

								{classification && classification === "Government Agency" && (
									<div className="col-span-12 flex">
										<FormField
											control={form.control}
											name="corporation"
											render={({ field }) => (
												<FormItem>
													<div className="flex flex-col w-full">
														<FormLabel required>Name of Government Agency</FormLabel>
														<FormDescription />
													</div>
													<FormGroup iconPosition="left">
														<Input autoComplete="off" {...field} disabled={emailExists} onChange={(e) => field.onChange(e.target.value.toUpperCase())} />
													</FormGroup>
													<FormMessage />
												</FormItem>
											)}
										/>
									</div>
								)}
								<div className="col-span-12 flex">
									<FormField
										control={form.control}
										name="mobile_number"
										render={({ field }) => (
											<FormItem>
												<div className="flex flex-col w-full">
													<FormLabel>Mobile Number</FormLabel>
												</div>
												<FormGroup iconPosition="left">
													<FormPrepend>+639</FormPrepend>
													<Input
														{...field}
														autoComplete="off"
														placeholder=""
														maxLength={9}
														onChange={(e) => {
															form.trigger("roll_number");
															const onlyNumbers = e.target.value.replace(/\D/g, "");
															field.onChange(onlyNumbers);
														}}
													/>
												</FormGroup>
												<FormMessage />
											</FormItem>
										)}
									/>
								</div>
								<div className="col-span-12 flex items-center text-[14px] text-gray-500">
									<div className="flex flex-col w-full">
										<span className="text-lg font-semibold text-foreground-dark">Defendant Address</span>
										<span className="text-base text-foreground/70">
											Fill in the full address details of the defendant to ensure that legal documents are properly served.
										</span>
									</div>
								</div>

								<div className="col-span-12 flex">
									<FormField
										control={form.control}
										name="address_type"
										render={({ field }) => (
											<FormItem>
												<div className="flex flex-col w-full">
													<FormLabel required>Address Type</FormLabel>
													<FormDescription>Select type of address</FormDescription>
												</div>
												<FormGroup>
													<Select
														onValueChange={field.onChange}
														defaultValue={field.value}
														options={[
															{
																value: "domestic",
																label: "Domestic",
															},
															{
																value: "international",
																label: "International",
															},
														]}
													/>
												</FormGroup>
												<FormMessage />
											</FormItem>
										)}
									/>
								</div>

								{addressType === "domestic" && (
									<>
										<div className="col-span-6 flex">
											<FormField
												control={form.control}
												name="region_code"
												render={({ field }) => (
													<FormItem>
														<div className="flex flex-col w-full">
															<FormLabel required>Region</FormLabel>
															<FormDescription />
														</div>
														<FormGroup>
															<Combobox
																defaultValue={field.value}
																returnFullOption
																onValueChange={(value) => {
																	form.setValue("province_code", "");
																	form.setValue("city_code", "");

																	if (value) {
																		field.onChange(value.value!);
																		form.setValue("region", {
																			name: value.label,
																			code: value.value,
																		});
																	}
																}}
																placeholderValue={{
																	label: data?.region?.name,
																	value: data?.region?.code,
																}}
																loadOptions={async (value: string) => {
																	const items = await regionService.list({
																		"filter[search]": value,
																	});

																	return items.data.map((item) => ({
																		label: item.name,
																		value: item.code,
																	}));
																}}
															/>
														</FormGroup>
														<FormMessage />
													</FormItem>
												)}
											/>
										</div>

										<div className="col-span-6 flex">
											<FormField
												control={form.control}
												name="province_code"
												render={({ field }) => (
													<FormItem>
														<div className="flex flex-col w-full">
															<FormLabel required>Province</FormLabel>
															<FormDescription />
														</div>
														<FormGroup>
															<Combobox
																defaultValue={field.value}
																disabled={!regionCode}
																returnFullOption
																key={regionCode}
																onValueChange={(value) => {
																	form.setValue("city_code", "");

																	if (value) {
																		field.onChange(value.value);
																		form.setValue("province", {
																			name: value.label,
																			code: value.value,
																			region_code: regionCode,
																		});
																	}
																}}
																placeholderValue={{
																	label: data?.province?.name,
																	value: data?.province?.code,
																}}
																loadOptions={async (value: string) => {
																	const items = await provinceService.list({
																		"filter[search]": value,
																		"filter[region_code]": regionCode,
																	});

																	return items.data.map((item) => ({
																		label: item.name,
																		value: item.code,
																	}));
																}}
															/>
														</FormGroup>
														<FormMessage />
													</FormItem>
												)}
											/>
										</div>

										<div className="col-span-6 flex">
											<FormField
												control={form.control}
												name="city_code"
												render={({ field }) => (
													<FormItem>
														<div className="flex flex-col w-full">
															<FormLabel required>City</FormLabel>
															<FormDescription />
														</div>
														<FormGroup>
															<Combobox
																defaultValue={field.value}
																disabled={!provinceCode}
																key={provinceCode}
																returnFullOption
																onValueChange={(value) => {
																	if (value) {
																		field.onChange(value.value);
																		form.setValue("city", {
																			name: value.label,
																			code: value.value,
																			province_code: provinceCode,
																		});
																	}
																}}
																placeholderValue={{
																	label: data?.city?.name,
																	value: data?.city?.code,
																}}
																loadOptions={async (value: string) => {
																	const items = await cityService.list({
																		"filter[search]": value,
																		"filter[province_code]": provinceCode,
																	});

																	return items.data.map((item) => ({
																		label: item.name,
																		value: item.code,
																	}));
																}}
															/>
														</FormGroup>
														<FormMessage />
													</FormItem>
												)}
											/>
										</div>

										<div className="col-span-6 flex">
											<FormField
												control={form.control}
												name="barangay"
												render={({ field }) => (
													<FormItem>
														<FormLabel required>Barangay</FormLabel>
														<FormGroup iconPosition="left">
															<Input autoComplete="off" {...field} />
														</FormGroup>
														<FormMessage />
													</FormItem>
												)}
											/>
										</div>

										<div className="col-span-12 flex">
											<FormField
												control={form.control}
												name="address_1"
												render={({ field }) => (
													<FormItem>
														<FormLabel required>Street/Lot No./Building/Room No.</FormLabel>
														<FormGroup iconPosition="left">
															<Input autoComplete="off" {...field} />
														</FormGroup>
														<FormMessage />
													</FormItem>
												)}
											/>
										</div>

										<div className="col-span-6 flex">
											<FormField
												control={form.control}
												name="address_2"
												render={({ field }) => (
													<FormItem>
														<FormLabel>Apartment/Suite/Unit</FormLabel>
														<FormGroup iconPosition="left">
															<Input autoComplete="off" {...field} />
														</FormGroup>
														<FormMessage />
													</FormItem>
												)}
											/>
										</div>

										<div className="col-span-6 flex">
											<FormField
												control={form.control}
												name="zipcode"
												render={({ field }) => (
													<FormItem>
														<FormLabel>Zip Code</FormLabel>
														<FormGroup iconPosition="left">
															<NumericFormat
																decimalScale={2}
																thousandsGroupStyle="thousand"
																thousandSeparator={false}
																className={clsx(
																	"block w-full rounded-md border-gray-300 text-gray-500 focus:border-blue-400 focus:ring-blue-400 sm:text-sm",
																	"disabled:border-slate-200 disabled:bg-slate-100 disabled:shadow-none",
																)}
																{...field}
																onValueChange={(values) => {
																	field.onChange(values.floatValue);
																}}
															/>
														</FormGroup>
														<FormMessage />
													</FormItem>
												)}
											/>
										</div>
									</>
								)}

								{addressType === "international" && (
									<>
										<div className="col-span-12 flex">
											<FormField
												control={form.control}
												name="country"
												render={({ field }) => (
													<FormItem>
														<div className="flex flex-col w-full">
															<FormLabel required>Country</FormLabel>
															<FormDescription />
														</div>
														<FormGroup>
															<Select
																onValueChange={async (value) => {
																	field.onChange(value);
																}}
																defaultValue={field.value}
																options={countries.map((c) => ({
																	label: c.name,
																	value: c.name,
																}))}
															/>
														</FormGroup>
													</FormItem>
												)}
											/>
										</div>

										<div className="col-span-12 flex">
											<FormField
												control={form.control}
												name="address_1"
												render={({ field }) => (
													<FormItem>
														<FormLabel required>Address</FormLabel>
														<FormGroup iconPosition="left">
															<Input disabled={form.watch("country") === ""} autoComplete="off" {...field} />
														</FormGroup>
														<FormMessage />
													</FormItem>
												)}
											/>
										</div>
									</>
								)}
							</div>
						</div>
					</div>
				</div>
				<div className="flex flex-row justify-between w-full gap-4 mt-4">
					<Button variant="ghost" type="button" onClick={closeModal}>
						Cancel
					</Button>
					<Button type="submit" disabled={!isEdit && sameEmail && undef} loading={form.formState.isSubmitting}>
						Confirm
					</Button>
				</div>
			</form>
		</Form>
	);
}
